import { FC, FunctionComponent, useEffect, useState } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';
import {
    ErrorBoundary,
    PermissionRoute,
    PermissionRouteProps,
    ErrorPage,
    ProtectedRoute,
    CookieBanner,
    MainLayout,
    Login,
    ResetPassword,
    ForgottenPassword,
    Permission,
    useLogs,
    useAuth,
} from '@isocel/front-common';

import './assets/styles/App.less';
import { getRawRoute, RoutePathName } from './routes';
import Home from './pages/home';
/* import { Dashboard } from './pages/dashboard'; */
import ShipmentTrack from './pages/shipment/ShipmentTrack';
import { ShipmentSuccess, Support } from './pages/shipment';
import ShipmentType from './pages/shipmentType';
import BeforeLoading from './pages/beforeLoading';
import AfterLoading from './pages/afterLoading/AfterLoading';
import DeliveryInfo from './pages/receptionStore/deliveryInformation/DeliveryInfo';
import { ReceptionDetails, ReceptionInfo, ReceptionView } from './pages/receptionStore';
import Registration from './pages/receptionStore/registration/Registration';
import Dashboard from './pages/dashboard/Dashboard';
import { PackagingRecipient, RecipientMovement, RecipientTrack } from './pages/packingMovement';
import ReceptionStore from './pages/receptionStore/receptionStore/ReceptionStore';
import ReceptionFromSupplier from './pages/receptionStore/receptionFromSupplier/ReceptionFromSupplier';
import ReceptionComment from './pages/packingMovement/ReceptionComment';
import { PalettCreate } from './pages/palettManagement';
import { Qrparc, QrReading } from './pages/qrReading';
import QrPackageTrack from './pages/qrReading/QrPackageTrack';
import QrPackageUpdate from './pages/qrReading/QrPackageUpdate';
import QrCameraPage from './pages/qrReading/QrCamera';
import { getSelectedKey, menuItems } from './config/menu-items';

const routes: Array<PermissionRouteProps & { Children: FunctionComponent }> = [
    {
        path: getRawRoute(RoutePathName.home),
        Children: Home,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.dashboard),
        permission: Permission.dashboard,
        Children: Dashboard,
    },
    {
        path: getRawRoute(RoutePathName.shipmentType),
        Children: ShipmentType,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.receptionComment),
        Children: ReceptionComment,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.receptionDetails),
        Children: ReceptionDetails,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.receptionView),
        Children: ReceptionView,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.successShipment),
        Children: ShipmentSuccess,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.receptionView),
        Children: ReceptionView,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.recipient),
        permission: Permission.storeUser,
        Children: PackagingRecipient,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.recipientMovement),
        Children: RecipientMovement,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.recipientTrack),
        Children: RecipientTrack,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.receptionInfo),
        permission: Permission.orgUser,
        Children: ReceptionInfo,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.supportTrack),
        Children: Support,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.shipmentTrack),
        Children: ShipmentTrack,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.receptionStore),
        permission: Permission.storeUser,
        Children: ReceptionStore,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.deliveryInfo),
        Children: DeliveryInfo,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.receptionFromSupplier),
        Children: ReceptionFromSupplier,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.beforeLoading),
        permission: Permission.orgUser,
        Children: BeforeLoading,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.afterLoading),
        permission: Permission.orgUser,
        Children: AfterLoading,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.registration),
        Children: Registration,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.palettCreate),
        permission: Permission.orgUser,
        Children: PalettCreate,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.qrReading),
        Children: QrReading,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.qrParcs),
        permission: Permission.orgUser,
        Children: Qrparc,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.qrPackageTrack),
        Children: QrPackageTrack,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.qrPackageUpdate),
        Children: QrPackageUpdate,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.qrCamera),
        Children: QrCameraPage,
        exact: true,
    },
];

const App: FC = () => {
    const { sendLogs } = useLogs();
    const { pathname } = useLocation();
    const isExportRoute = pathname.startsWith('/exports/');
    const [menuData, setMenuData] = useState([]);
    const { user } = useAuth();

    useEffect(() => {
        setMenuData([...menuItems] as any);
    }, [user]);
    const pageContent = (
        <ErrorBoundary sendLogs={sendLogs}>
            <Switch>
                {routes.map(({ Children, ...props }) => {
                    return (
                        <PermissionRoute
                            key={Array.isArray(props.path) ? props.path.join('') : props.path?.toString()}
                            {...props}
                        >
                            <Children />
                        </PermissionRoute>
                    );
                })}

                <Route path="*">
                    <ErrorPage />
                </Route>
            </Switch>
        </ErrorBoundary>
    );
    return (
        <Switch>
            <Route exact path={getRawRoute(RoutePathName.login)} component={Login} />
            <Route exact path={getRawRoute(RoutePathName.forgottenPassword)} component={ForgottenPassword} />
            <Route exact path={getRawRoute(RoutePathName.resetPassword)} component={ResetPassword} />

            <ProtectedRoute>
                {isExportRoute ? (
                    <div id="export-wrapper">{pageContent}</div>
                ) : (
                    <>
                        <CookieBanner />
                        <MainLayout menuItems={menuData} getSelectedKey={getSelectedKey}>
                            {pageContent}
                        </MainLayout>
                    </>
                )}
            </ProtectedRoute>
        </Switch>
    );
};

export default App;
